import type { UserAttributes } from "@supabase/supabase-js"
import { useMutation } from "@tanstack/react-query"

import { useStableFunction } from "@repo/ui/hooks/use-stable-function"
import { useRefreshSession } from "./use-refresh-session"
import { queryKey as sessionQueryKey } from "./use-session"
import { useSupabase } from "./use-supabase"
import { queryKey as userQueryKey } from "./use-user"

const mutationKey = ["auth", "update-user"]

type UpdateUserParams = {
  attributes: UserAttributes
  options?: {
    emailRedirectTo?: string
    captchaToken?: string
  }
}

export type UseUpdateUserProps = {
  onSuccess?: VoidFunction
  onError?: (error: Error) => void
}

export function useUpdateUser({ onSuccess, onError }: UseUpdateUserProps = {}) {
  const supabase = useSupabase()
  const refreshSession = useRefreshSession()

  const mutationFn = useStableFunction(
    async ({ attributes, options }: UpdateUserParams) => {
      const { data, error } = await supabase.auth.updateUser(
        attributes,
        options,
      )

      if (error) {
        throw error
      }

      await refreshSession.mutateAsync(undefined)

      return data
    },
  )

  return useMutation({
    meta: {
      invalidates: [userQueryKey, sessionQueryKey],
    },
    mutationKey,
    mutationFn,
    onSuccess,
    onError,
  })
}
